import { Paper } from '../../../kit/Paper';
import { DateFmt, dateText, Text } from '../../../kit/Text';
import { DateUtils } from '../../../core/utils/dateUtils';
import { Layout } from '../../../kit/Layout';
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Image } from '../../../kit/Image';
import { IconGreenLight, IconOrangeLight, IconRedLight } from '../../../kit/Icons/Lights';
import { differenceInDays } from 'date-fns';

export type ChartRow = {
  chartId: number;
  lastChartUpdate?: Date;
  nextChartReview?: Date;
  facilityGroupId: number;
  facilityName?: string;
  patientHsId: number;
  patientRoomNumber?: string;
  patientImageUrl?: string;
  patientGivenName: string;
  patientFamilyName: string;
  patientPreferredName: string;
};

function getReviewAndUpdateDays(chartRow: ChartRow) {
  const lastChartUpdateDays = chartRow.lastChartUpdate
    ? differenceInDays(new Date(), chartRow.lastChartUpdate)
    : undefined;
  const nextChartReviewDays = chartRow.nextChartReview
    ? differenceInDays(chartRow.nextChartReview, new Date())
    : undefined;

  return {
    lastChartUpdateDays: lastChartUpdateDays,
    nextChartReviewDays: nextChartReviewDays,
  };
}

export function hasReviewOrUpdate(chartRow: ChartRow) {
  const days = getReviewAndUpdateDays(chartRow);
  return (
    (days.lastChartUpdateDays !== undefined && days.lastChartUpdateDays < 1) ||
    (days.nextChartReviewDays !== undefined && days.nextChartReviewDays < 14)
  );
}

function ChartExpiredLabel() {
  return (
    <svg width="7rem" height="2rem" viewBox="0 0 114 31" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g>
        <rect width="114" height="31" rx="15.5" fill="#D10000" />
        <g transform="translate(10, 10)">
          <path
            y="20"
            d="M5.85603 9V7.356H2.60403V5.568H5.26803L5.00403 3.876H2.60403V2.364H5.74803V0.719999H0.588031V9H5.85603ZM9.01391 4.788L6.39791 9H8.49791L10.1299 6.168L11.8339 9.084L14.0299 8.736C14.0299 8.736 11.4259 4.62 11.4259 4.632L13.8379 0.719999H11.7259L10.2979 3.252C10.2979 3.24 8.79791 0.636 8.79791 0.636L6.61391 0.983999L9.01391 4.788ZM20.8595 3.42C20.8595 1.62 19.6235 0.719999 17.6075 0.719999H14.9435V9H16.9955V6.252H17.5235C19.5275 6.252 20.8595 5.292 20.8595 3.42ZM18.8675 3.456C18.8675 4.308 18.3635 4.704 17.4515 4.704H16.9715V2.268H17.4875C18.3995 2.268 18.8675 2.664 18.8675 3.456ZM23.9341 9V0.719999H21.8821V9H23.9341ZM29.5464 3.456C29.5464 4.248 29.0304 4.68 28.1544 4.68H27.5184V2.268H28.1784C29.0664 2.268 29.5464 2.688 29.5464 3.456ZM30.0264 5.688C31.0104 5.256 31.5624 4.476 31.5624 3.336C31.5624 1.632 30.3864 0.719999 28.3824 0.719999H25.4904V9H27.5304V6.156H28.0944L29.9064 9.084L32.0064 8.736L30.0264 5.688ZM38.1412 9V7.356H34.8892V5.568H37.5532L37.2892 3.876H34.8892V2.364H38.0332V0.719999H32.8732V9H38.1412ZM46.5311 4.848C46.5311 2.388 45.0191 0.719999 42.1391 0.719999H39.2951V9H42.1991C44.9951 9 46.5311 7.308 46.5311 4.848ZM44.4671 4.86C44.4671 6.636 43.5551 7.404 42.0911 7.404H41.3471V2.316H42.0911C43.4951 2.316 44.4671 3.12 44.4671 4.86ZM56.3358 8.532L56.0718 6.852C55.4478 7.2 54.8598 7.428 54.1878 7.428C52.9398 7.428 52.0158 6.516 52.0158 4.86C52.0158 3.24 52.8318 2.292 54.0798 2.292C54.7998 2.292 55.4238 2.58 55.9278 2.964L56.2158 1.116C55.6158 0.779999 54.8598 0.576 53.9238 0.576C51.5718 0.576 49.9638 2.304 49.9638 4.872C49.9638 7.464 51.4758 9.144 53.9838 9.144C54.8958 9.144 55.6638 8.904 56.3358 8.532ZM64.5014 9V0.719999H62.4494V3.816H59.5814V0.719999H57.5294V9H59.5814V5.616H62.4494V9H64.5014ZM73.5258 8.748L70.6458 0.684H68.3898L65.4138 9H67.4178L67.9938 7.284H70.8378L71.4618 9.084L73.5258 8.748ZM70.3458 5.676H68.4738L69.4098 2.796L70.3458 5.676ZM78.4956 3.456C78.4956 4.248 77.9796 4.68 77.1036 4.68H76.4676V2.268H77.1276C78.0156 2.268 78.4956 2.688 78.4956 3.456ZM78.9756 5.688C79.9596 5.256 80.5116 4.476 80.5116 3.336C80.5116 1.632 79.3356 0.719999 77.3316 0.719999H74.4396V9H76.4796V6.156H77.0436L78.8556 9.084L80.9556 8.736L78.9756 5.688ZM87.9955 2.46L87.7075 0.719999H81.1555V2.46H83.4475V9H85.5115V2.46H87.9955Z"
            fill="white"
          />
        </g>
      </g>
    </svg>
  );
}

export function ChartMedChangesLabel() {
  return (
    <svg width="100" height="31" viewBox="0 0 100 31" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g>
        <rect width="100" height="31" rx="15.5" fill="#D184FF" />
        <g transform="translate(10, 10)">
          <path
            d="M10.2014 9L9.60139 0.719999H7.02139L5.40139 6.42L3.79339 0.719999H1.14139L0.541391 9H2.32939L2.68939 3.492L4.26139 9.012H6.18139L7.75339 3.492L8.11339 9H10.2014ZM16.7369 9V7.356H13.4849V5.568H16.1489L15.8849 3.876H13.4849V2.364H16.6289V0.719999H11.4689V9H16.7369ZM25.1268 4.848C25.1268 2.388 23.6148 0.719999 20.7348 0.719999H17.8908V9H20.7948C23.5908 9 25.1268 7.308 25.1268 4.848ZM23.0628 4.86C23.0628 6.636 22.1508 7.404 20.6868 7.404H19.9428V2.316H20.6868C22.0908 2.316 23.0628 3.12 23.0628 4.86ZM34.9315 8.532L34.6675 6.852C34.0435 7.2 33.4555 7.428 32.7835 7.428C31.5355 7.428 30.6115 6.516 30.6115 4.86C30.6115 3.24 31.4275 2.292 32.6755 2.292C33.3955 2.292 34.0195 2.58 34.5235 2.964L34.8115 1.116C34.2115 0.779999 33.4555 0.576 32.5195 0.576C30.1675 0.576 28.5595 2.304 28.5595 4.872C28.5595 7.464 30.0715 9.144 32.5795 9.144C33.4915 9.144 34.2595 8.904 34.9315 8.532ZM43.0971 9V0.719999H41.0451V3.816H38.1771V0.719999H36.1251V9H38.1771V5.616H41.0451V9H43.0971ZM52.1215 8.748L49.2415 0.684H46.9855L44.0095 9H46.0135L46.5895 7.284H49.4335L50.0575 9.084L52.1215 8.748ZM48.9415 5.676H47.0695L48.0055 2.796L48.9415 5.676ZM59.9833 9V0.719999H58.0753V5.352L55.0273 0.719999H53.0353V9H54.9433V4.044L58.2073 9H59.9833ZM68.3002 8.28V4.248H64.8322L65.0722 5.772H66.5122V7.248C66.1762 7.428 65.8522 7.512 65.3962 7.512C63.9682 7.512 63.2482 6.42 63.2482 4.86C63.2482 3.312 63.9442 2.256 65.3722 2.256C66.2242 2.256 66.9562 2.568 67.5802 2.964L67.8562 1.176C67.1962 0.804 66.3442 0.564 65.3242 0.564C62.7682 0.564 61.1962 2.304 61.1962 4.872C61.1962 7.428 62.6482 9.156 65.2762 9.156C66.4162 9.156 67.4362 8.844 68.3002 8.28ZM74.9439 9V7.356H71.6919V5.568H74.3559L74.0919 3.876H71.6919V2.364H74.8359V0.719999H69.6759V9H74.9439ZM81.6178 6.504C81.6178 5.184 80.8978 4.56 79.0978 3.9C78.0658 3.504 77.8378 3.3 77.8378 2.88C77.8378 2.496 78.1378 2.232 78.7618 2.232C79.3978 2.232 80.1778 2.472 80.9218 2.904L81.2338 1.176C80.5138 0.827999 79.8178 0.624 78.7378 0.624C77.1538 0.624 75.9058 1.476 75.9058 3.072C75.9058 4.44 76.6258 4.98 78.2818 5.616C79.4458 6.06 79.6858 6.276 79.6858 6.744C79.6858 7.236 79.2778 7.488 78.6898 7.488C77.8618 7.488 76.9138 7.176 76.0618 6.72L75.7618 8.472C76.6018 8.856 77.5378 9.096 78.6538 9.096C80.2978 9.096 81.6178 8.304 81.6178 6.504Z"
            fill="black"
          />
        </g>
      </g>
    </svg>
  );
}

function ChartReviewPendingLabel() {
  return (
    <svg width="162" height="31" viewBox="0 0 162 31" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g>
        <rect width="162" height="31" rx="15.5" fill="#D184FF" />
        <g transform="translate(10, 10)">
          <path
            d="M7.18738 8.532L6.92338 6.852C6.29938 7.2 5.71138 7.428 5.03938 7.428C3.79138 7.428 2.86738 6.516 2.86738 4.86C2.86738 3.24 3.68338 2.292 4.93138 2.292C5.65138 2.292 6.27538 2.58 6.77938 2.964L7.06738 1.116C6.46738 0.779999 5.71138 0.576 4.77538 0.576C2.42338 0.576 0.815375 2.304 0.815375 4.872C0.815375 7.464 2.32738 9.144 4.83538 9.144C5.74738 9.144 6.51538 8.904 7.18738 8.532ZM15.353 9V0.719999H13.301V3.816H10.433V0.719999H8.381V9H10.433V5.616H13.301V9H15.353ZM24.3774 8.748L21.4974 0.684H19.2414L16.2654 9H18.2694L18.8454 7.284H21.6894L22.3134 9.084L24.3774 8.748ZM21.1974 5.676H19.3254L20.2614 2.796L21.1974 5.676ZM29.3472 3.456C29.3472 4.248 28.8312 4.68 27.9552 4.68H27.3192V2.268H27.9792C28.8672 2.268 29.3472 2.688 29.3472 3.456ZM29.8272 5.688C30.8112 5.256 31.3632 4.476 31.3632 3.336C31.3632 1.632 30.1872 0.719999 28.1832 0.719999H25.2912V9H27.3312V6.156H27.8952L29.7072 9.084L31.8072 8.736L29.8272 5.688ZM38.847 2.46L38.559 0.719999H32.007V2.46H34.299V9H36.363V2.46H38.847ZM46.3393 3.456C46.3393 4.248 45.8233 4.68 44.9473 4.68H44.3113V2.268H44.9713C45.8593 2.268 46.3393 2.688 46.3393 3.456ZM46.8193 5.688C47.8033 5.256 48.3553 4.476 48.3553 3.336C48.3553 1.632 47.1793 0.719999 45.1753 0.719999H42.2833V9H44.3233V6.156H44.8873L46.6993 9.084L48.7993 8.736L46.8193 5.688ZM54.9342 9V7.356H51.6822V5.568H54.3462L54.0822 3.876H51.6822V2.364H54.8262V0.719999H49.6662V9H54.9342ZM63.48 0.719999H61.368L59.568 6.444L57.72 0.636L55.488 0.972L58.392 9.036H60.492L63.48 0.719999ZM66.4966 9V0.719999H64.4446V9H66.4966ZM73.3209 9V7.356H70.0689V5.568H72.7329L72.4689 3.876H70.0689V2.364H73.2129V0.719999H68.0529V9H73.3209ZM85.3828 0.719999H83.3548L82.1668 5.904L80.8228 0.719999H78.7588L77.4148 5.904L76.1668 0.636L73.9468 0.983999L76.1308 9H78.1948L79.6228 3.66L81.0508 9H83.1268L85.3828 0.719999ZM94.7814 3.42C94.7814 1.62 93.5454 0.719999 91.5294 0.719999H88.8654V9H90.9174V6.252H91.4454C93.4494 6.252 94.7814 5.292 94.7814 3.42ZM92.7894 3.456C92.7894 4.308 92.2854 4.704 91.3734 4.704H90.8934V2.268H91.4094C92.3214 2.268 92.7894 2.664 92.7894 3.456ZM101.024 9V7.356H97.772V5.568H100.436L100.172 3.876H97.772V2.364H100.916V0.719999H95.756V9H101.024ZM109.126 9V0.719999H107.218V5.352L104.17 0.719999H102.178V9H104.086V4.044L107.35 9H109.126ZM117.875 4.848C117.875 2.388 116.363 0.719999 113.483 0.719999H110.639V9H113.543C116.339 9 117.875 7.308 117.875 4.848ZM115.811 4.86C115.811 6.636 114.899 7.404 113.435 7.404H112.691V2.316H113.435C114.839 2.316 115.811 3.12 115.811 4.86ZM121.2 9V0.719999H119.148V9H121.2ZM129.704 9V0.719999H127.796V5.352L124.748 0.719999H122.756V9H124.664V4.044L127.928 9H129.704ZM138.021 8.28V4.248H134.553L134.793 5.772H136.233V7.248C135.897 7.428 135.573 7.512 135.117 7.512C133.689 7.512 132.969 6.42 132.969 4.86C132.969 3.312 133.665 2.256 135.093 2.256C135.945 2.256 136.677 2.568 137.301 2.964L137.577 1.176C136.917 0.804 136.065 0.564 135.045 0.564C132.489 0.564 130.917 2.304 130.917 4.872C130.917 7.428 132.369 9.156 134.997 9.156C136.137 9.156 137.157 8.844 138.021 8.28Z"
            fill="black"
          />
        </g>
      </g>
    </svg>
  );
}

export function ChartCard(props: ChartRow) {
  const days = getReviewAndUpdateDays(props);

  const lastChartUpdateDays = days.lastChartUpdateDays;
  const nextChartReviewDays = days.nextChartReviewDays;

  const profileLink = `/resident-details/${props.patientHsId}`;

  return (
    <Paper secondary>
      <InformationBox horizontal gap={1}>
        <PatientImageColumn>
          {props.patientImageUrl && (
            <Link to={profileLink}>
              <PatientImage src={props.patientImageUrl} facilityGroupId={props.facilityGroupId} />
            </Link>
          )}
        </PatientImageColumn>
        <PatientDetailColumn>
          <Text weight={'bold'}>
            {props.patientGivenName} {props.patientFamilyName}{' '}
            {props.patientPreferredName && `(${props.patientPreferredName})`}
          </Text>
          <Text>
            {props.facilityName} Rm {props.patientRoomNumber}
          </Text>
        </PatientDetailColumn>
        <LastChartUpdateColumn gap={0.5}>
          <LastChartUpdateDetail lastChartUpdate={props.lastChartUpdate} />
        </LastChartUpdateColumn>
        <ChartReviewColumn gap={0.5}>
          <ChartReviewDetail nextChartReview={props.nextChartReview} nextChartReviewDays={nextChartReviewDays} />
        </ChartReviewColumn>
        <ChartActionsColumn>
          <ChartActionsDetail nextChartReviewDays={nextChartReviewDays} lastChartUpdateDays={lastChartUpdateDays} />
        </ChartActionsColumn>
      </InformationBox>
    </Paper>
  );
}

function LastChartUpdateDetail(props: { lastChartUpdate?: Date }) {
  return (
    <>
      <Text weight={'bold'}>Last chart update</Text>
      {props.lastChartUpdate && (
        <Text>
          {DateUtils.dateTo24HourTimeString(props.lastChartUpdate)},{' '}
          {dateText(props.lastChartUpdate, DateFmt.SimpleDate)}
        </Text>
      )}
    </>
  );
}

function ChartReviewDetail(props: { nextChartReview?: Date; nextChartReviewDays?: number }) {
  return (
    <>
      <Layout horizontal gap={0.5}>
        {props.nextChartReviewDays &&
          (props.nextChartReviewDays < 1 ? (
            <IconRedLight />
          ) : props.nextChartReviewDays < 14 ? (
            <IconOrangeLight />
          ) : (
            <IconGreenLight />
          ))}
        <Text>Chart Review</Text>
      </Layout>
      <Text>{props.nextChartReview ? dateText(props.nextChartReview, DateFmt.SimpleDate) : 'N/A'}</Text>
    </>
  );
}

function ChartActionsDetail(props: { nextChartReviewDays?: number; lastChartUpdateDays?: number }) {
  return (
    <Layout gap={0.2}>
      {props.nextChartReviewDays && props.nextChartReviewDays < 0 ? <ChartExpiredLabel /> : <></>}
      {props.nextChartReviewDays && props.nextChartReviewDays > 0 && props.nextChartReviewDays < 14 ? (
        <ChartReviewPendingLabel />
      ) : (
        <></>
      )}
      {props.lastChartUpdateDays !== undefined && props.lastChartUpdateDays < 1 ? <ChartMedChangesLabel /> : <></>}
    </Layout>
  );
}

const PatientImage = styled(Image)`
  height: 60px;
  width: 60px;
  object-fit: cover;
  border-radius: 999999999px;
`;

const InformationBox = styled(Layout)`
  display: flex;
  align-items: center;
`;

const PatientImageColumn = styled(Layout)`
  padding-right: 1.25rem;
`;

const PatientDetailColumn = styled(Layout)`
  padding-right: 1.25rem;
  flex-basis: 15%;
`;

const LastChartUpdateColumn = styled(Layout)`
  padding-right: 1.25rem;
  flex-basis: 15%;
`;

const ChartReviewColumn = styled(Layout)`
  flex-basis: 55%;
  padding: 1.25rem;
  align-items: center;
`;

const ChartActionsColumn = styled(Layout)`
  padding-right: 1.25rem;
  flex-basis: 15%;
`;
